import { IconButton, Tooltip } from '@mui/material';
import { Transcribe } from '@mui/icons-material';
import { SideDrawer } from '@/components/common';
import CallTranscriptContent from './CallTranscriptContent';

import type { SideDrawerControls } from '@/components/common/SideDrawer';
import type { ParsedTranscript } from '@/types/transcript';
import { useDateFormatter } from '@/hooks';

interface CallTranscriptDrawerProps {
    parsedTranscript?: ParsedTranscript | null;
    sideDrawerControls: SideDrawerControls;
}

/**
 * Component that renders a side drawer with a transcript of a call if available.
 *
 * @param {CallTranscriptDrawerProps} props
 * @returns {JSX.Element}
 */
const CallTranscriptDrawer = ({ parsedTranscript = null, sideDrawerControls }: CallTranscriptDrawerProps) => {
    const { isOpen, toggle, close } = sideDrawerControls;
    const formatDate = useDateFormatter();

    const generatedAt = formatDate(parsedTranscript?.created_at ?? null);
    const subtitle = generatedAt ? `Transcript generated ${generatedAt}` : null;
    const transcript = Array.isArray(parsedTranscript?.transcript) ? parsedTranscript?.transcript : null;

    const tooltipTitle = isOpen ? 'Close audio transcript' : 'Open audio transcript';
    const buttonAriaLabel = isOpen ? 'Close transcript' : 'Open transcript';

    return (
        <>
            {transcript && (
                <SideDrawer
                    isOpen={isOpen}
                    onClose={close}
                    title="Audio transcript"
                    subtitle={subtitle}
                    testId="call-transcript-drawer"
                    closeButtonAriaLabel="Close transcript"
                >
                    <CallTranscriptContent transcript={transcript} />
                </SideDrawer>
            )}
            <Tooltip
                title={transcript ? tooltipTitle : 'No transcript available'}
                placement="top"
                data-testid="transcript-toggle-tooltip"
                arrow
            >
                <span>
                    <IconButton onClick={toggle} disabled={!transcript} aria-expanded={isOpen} aria-label={buttonAriaLabel}>
                        <Transcribe fontSize="medium" color={transcript ? 'primary' : 'disabled'} />
                    </IconButton>
                </span>
            </Tooltip>
        </>
    );
};

export default CallTranscriptDrawer;
